/**
 * The tour overlay: a spotlight cut around the current anchor, plus a popover
 * with the step copy and Back / Next / Done.
 *
 * Web/desktop only. It reads the anchor's live `getBoundingClientRect()` rather
 * than RN's `measureInWindow`, because the sidebar animates its width on hover
 * and the rect has to stay glued to a moving target — a DOM read is exact and
 * costs nothing at the poll rate used here.
 *
 * The dim is drawn as four rects around the hole rather than one rect with a
 * cut-out, so the anchor stays fully un-dimmed and clickable. Letting people
 * click the thing being explained is the point; the step's own `navigate` puts
 * them back in place on the next step either way.
 *
 * Exits: the × button, Esc, clicking the dim, or advancing past the last step.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useTheme } from '../hooks/useTheme';
import Icon from '../components/Icon';
import { useTour } from './TourContext';
import {
  MARGIN as MARGIN_FALLBACK,
  POPOVER_WIDTH,
  popoverPosition,
  resolvePlacement,
  spotlightHole,
  type Rect,
} from './geometry';

const POPOVER_FALLBACK_HEIGHT = 188;
/** How long to wait for an anchor to appear after navigating before giving up. */
const ANCHOR_TIMEOUT_MS = 2500;
const ANCHOR_POLL_MS = 80;
/** Re-read the rect at this cadence so hover/scroll/resize keep the ring glued. */
const TRACK_MS = 120;

const isWebLike = Platform.OS === 'web';

function readRect(id: string): Rect | null {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(id);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // A zero-sized rect means the element is in the tree but not laid out yet
  // (or display:none) — treat it as "not ready" so we keep polling.
  if (r.width <= 0 || r.height <= 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export default function TourOverlay() {
  const theme = useTheme();
  const { width: vw, height: vh } = useWindowDimensions();
  const { active, step, index, steps, next, prev, end, skipCurrent } = useTour();

  const [rect, setRect] = useState<Rect | null>(null);
  const [popHeight, setPopHeight] = useState(POPOVER_FALLBACK_HEIGHT);
  // skipCurrent mutates the step list, so the effect that calls it must not
  // also depend on it re-running — a ref keeps the latest without re-arming.
  const skipRef = useRef(skipCurrent);
  skipRef.current = skipCurrent;

  const anchorId = step?.anchor ?? null;

  // ── Acquire the anchor, then track it ────────────────────────────────────
  useEffect(() => {
    if (!active || !anchorId || !isWebLike) { setRect(null); return; }
    // A new anchor means the old rect is a lie about a different element —
    // drop it BEFORE acquiring. Until the new anchor lands, the render below
    // shows a full dim with the popover centered (copy readable immediately),
    // never the previous step's spotlight under this step's words.
    setRect(null);

    let cancelled = false;
    let trackTimer: ReturnType<typeof setInterval> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const startedAt = Date.now();

    const beginTracking = () => {
      trackTimer = setInterval(() => {
        if (cancelled) return;
        const r = readRect(anchorId);
        // A disappearing anchor (navigation away mid-step) just freezes the
        // last known rect rather than collapsing the overlay mid-read.
        if (r) setRect(r);
      }, TRACK_MS);
    };

    const acquire = () => {
      if (cancelled) return;
      const r = readRect(anchorId);
      if (r) {
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        // Steps deep in a scroll view (Settings → About) need bringing up.
        try {
          document.getElementById(anchorId)?.scrollIntoView({ block: 'center', inline: 'nearest' });
        } catch {/* older engines: the rect is still usable as-is */}
        // Re-read after the scroll so the ring lands on the settled position.
        setRect(readRect(anchorId) ?? r);
        beginTracking();
        return;
      }
      if (Date.now() - startedAt > ANCHOR_TIMEOUT_MS) {
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        skipRef.current();
      }
    };

    acquire();
    if (!cancelled && !trackTimer) pollTimer = setInterval(acquire, ANCHOR_POLL_MS);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      if (trackTimer) clearInterval(trackTimer);
    };
  }, [active, anchorId]);

  // ── Keyboard ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active || !isWebLike || typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTextInput =
        target?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select';
      if (isTextInput) return;

      if (e.key === 'Escape') { e.preventDefault(); end(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, next, prev, end]);

  const onPopoverLayout = useCallback((e: any) => {
    const h = e?.nativeEvent?.layout?.height;
    if (h && Math.abs(h - popHeight) > 1) setPopHeight(h);
  }, [popHeight]);

  if (!active || !step || !isWebLike) return null;

  const total = steps.length;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  // While the anchor is still being acquired there is no rect: dim the whole
  // screen and center the popover so the step's words are readable at once.
  // The ring appears only when it has a real element to ring. If the anchor
  // never appears, the acquire effect drops the step after its timeout.
  const acquiring = !rect;
  const hole = rect ? spotlightHole(rect) : null;
  const placement = rect
    ? resolvePlacement(step.placement ?? 'right', rect, popHeight, vw, vh)
    : null;
  const pos = rect && placement
    ? popoverPosition(placement, rect, popHeight, vw, vh)
    : {
        top: Math.max(MARGIN_FALLBACK, vh / 2 - popHeight / 2),
        left: Math.max(MARGIN_FALLBACK, vw / 2 - POPOVER_WIDTH / 2),
      };

  const dim = 'rgba(4, 8, 18, 0.62)';
  const dimStyle = { backgroundColor: dim };

  return (
    <View style={styles.root} pointerEvents="box-none">
      {acquiring || !hole ? (
        <Pressable style={[styles.abs, dimStyle, { top: 0, left: 0, right: 0, bottom: 0 }]} onPress={end} />
      ) : (
        <>
          {/* Dim in four pieces so the anchor itself is neither dimmed nor covered
              — the hole is a genuine gap, and clicks fall through to the real UI. */}
          <Pressable
            style={[styles.abs, dimStyle, { top: 0, left: 0, right: 0, height: hole.top }]}
            onPress={end}
          />
          <Pressable
            style={[styles.abs, dimStyle, { top: hole.top + hole.height, left: 0, right: 0, bottom: 0 }]}
            onPress={end}
          />
          <Pressable
            style={[styles.abs, dimStyle, { top: hole.top, left: 0, width: hole.left, height: hole.height }]}
            onPress={end}
          />
          <Pressable
            style={[
              styles.abs, dimStyle,
              { top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height },
            ]}
            onPress={end}
          />

          {/* Spotlight ring */}
          <View
            pointerEvents="none"
            style={[
              styles.abs,
              styles.ring,
              hole,
              { borderColor: theme.colors.primary },
              Platform.OS === 'web'
                ? ({ boxShadow: `0 0 0 3px ${theme.colors.primary}33, 0 8px 28px rgba(0,0,0,0.28)` } as any)
                : null,
            ]}
          />
        </>
      )}

      {/* Popover */}
      <View
        onLayout={onPopoverLayout}
        style={[
          styles.abs,
          styles.popover,
          {
            top: pos.top,
            left: pos.left,
            backgroundColor: theme.colors.card,
            borderColor: theme.colors.border,
          },
          Platform.OS === 'web'
            ? ({ boxShadow: '0 20px 50px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.16)' } as any)
            : null,
        ]}
      >
        <View style={styles.popHeader}>
          <Text style={[styles.counter, { color: theme.colors.muted }]}>
            {index + 1} of {total}
          </Text>
          <TouchableOpacity
            onPress={end}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel="End tour"
          >
            <Icon icon={['fas', 'xmark']} size={13} color={theme.colors.muted} />
          </TouchableOpacity>
        </View>

        <Text style={[styles.title, { color: theme.colors.text }]}>{step.title}</Text>
        <Text style={[styles.body, { color: theme.colors.muted }]}>{step.body}</Text>

        <View style={styles.actions}>
          <TouchableOpacity
            onPress={end}
            style={styles.textBtn}
            accessibilityRole="button"
          >
            <Text style={[styles.textBtnLabel, { color: theme.colors.muted }]}>Skip</Text>
          </TouchableOpacity>

          <View style={styles.actionsRight}>
            {!isFirst && (
              <TouchableOpacity
                onPress={prev}
                style={[styles.secondaryBtn, { borderColor: theme.colors.border }]}
                accessibilityRole="button"
              >
                <Text style={[styles.secondaryLabel, { color: theme.colors.text }]}>Back</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              onPress={isLast ? end : next}
              style={[styles.primaryBtn, { backgroundColor: theme.colors.primary }]}
              accessibilityRole="button"
            >
              <Text style={[styles.primaryLabel, { color: theme.colors.primaryText || '#fff' }]}>
                {isLast ? 'Done' : 'Next'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    // Above the widget frame, the sidebar, and any in-screen sticky chrome.
    zIndex: 9000,
    ...Platform.select({ web: { position: 'fixed' } as any, default: {} }),
  },
  abs: { position: 'absolute' },
  ring: {
    borderWidth: 2,
    borderRadius: 12,
  },
  popover: {
    width: POPOVER_WIDTH,
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 18,
    paddingTop: 14,
    paddingBottom: 14,
  },
  popHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  counter: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
  },
  body: {
    fontSize: 13.5,
    lineHeight: 20,
    marginBottom: 16,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actionsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  textBtn: { paddingVertical: 6, paddingRight: 8 },
  textBtnLabel: { fontSize: 13, fontWeight: '600' },
  secondaryBtn: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 9,
    borderWidth: StyleSheet.hairlineWidth,
  },
  secondaryLabel: { fontSize: 13, fontWeight: '600' },
  primaryBtn: {
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 9,
  },
  primaryLabel: { fontSize: 13, fontWeight: '700' },
});
